import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboardingGate } from '../hooks/useOnboardingGate';
import { useRetailAccounts } from '../hooks/useRetailAccounts';
import {
  FEATURE_KEYS,
  useFeatureFlag,
} from '../contexts/FeatureFlagsContext';
import ChangePassword from '../components/profile/ChangePassword';
import PortfoliosRoutes from '../components/profile/PortfoliosRoutes';
import AccessDeniedPage from './AccessDeniedPage';
import './ProfilePage.css';

const portfoliosIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="2" x2="12" y2="22"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const compareIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2.5" y="5" width="8" height="14" rx="2" />
    <path d="M4.5 15.5 6.5 11.5 8 13" />
    <rect x="13.5" y="5" width="8" height="14" rx="2" />
    <path d="M15.5 13 17.5 9 19 10.5" />
  </svg>
);

const settingsIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const ProfilePage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const portfolioEnabled = useFeatureFlag(FEATURE_KEYS.portfolioMaster);
  const { accounts } = useRetailAccounts();
  const needsOnboarding = useOnboardingGate(Boolean(user) && user?.role !== 'admin');

  useEffect(() => {
    if (needsOnboarding === true) {
      navigate('/onboarding', { replace: true });
    }
  }, [needsOnboarding, navigate]);

  if (needsOnboarding === null || needsOnboarding === true) {
    return null;
  }

  const showCompareNav = portfolioEnabled && accounts.length > 1;
  const path = location.pathname;

  const isPortfoliosIndex =
    path === '/profile/portfolios' || path === '/profile/portfolios/';
  const isCompareSection = path.startsWith('/profile/portfolios/compare');

  const navItemBase =
    'flex w-full min-h-[44px] items-center gap-4 rounded-[var(--radius-lg)] border border-transparent bg-transparent px-6 py-5 text-left text-sm font-medium leading-none text-muted-foreground transition-colors cursor-pointer hover:bg-white/[0.04] hover:text-foreground';
  const navItemActive =
    'border-secondary/28 bg-secondary/10 text-foreground';
  const navIconBase =
    'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center text-[color:var(--nq-blue-bright)] opacity-85 [&>svg]:block [&>svg]:h-5 [&>svg]:w-5';
  const navIconActive = 'text-secondary opacity-100';

  return (
    <div className="min-h-[calc(100vh-var(--app-header-height))] w-full bg-transparent">
      <div className="mx-auto box-border flex min-h-[calc(100vh-var(--app-header-height))] max-w-[var(--content-max,1400px)] flex-col gap-6 p-6 pb-8 md:flex-row md:gap-12 md:px-6 md:pb-16 md:pt-6">
        <aside className="flex w-full flex-shrink-0 flex-col overflow-hidden md:sticky md:top-[calc(var(--app-header-height)+24px)] md:max-h-[calc(100vh-var(--app-header-height)-24px)] md:w-[220px] md:self-start">
          <div className="px-0 pb-4 md:px-4 md:pb-6">
            <h2 className="m-0 font-display text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Profile
            </h2>
          </div>
          <nav className="flex flex-1 flex-row gap-3 overflow-x-auto pb-2 md:flex-col md:gap-2 md:overflow-x-visible md:overflow-y-auto md:pb-0">
            {portfolioEnabled && (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => navigate('/profile/portfolios')}
                  className={`${navItemBase} flex-shrink-0 min-w-[148px] md:min-w-0 ${
                    isPortfoliosIndex ? navItemActive : ''
                  }`}
                >
                  <span className={`${navIconBase} ${isPortfoliosIndex ? navIconActive : ''}`}>
                    {portfoliosIcon}
                  </span>
                  <span className="flex-1 leading-5">My Portfolios</span>
                </button>
                {showCompareNav && (
                  <div className="flex flex-col gap-1 md:ml-6 md:border-l md:border-white/[0.08] md:pl-4">
                    <button
                      type="button"
                      onClick={() => navigate('/profile/portfolios/compare')}
                      className={`${navItemBase} min-h-[38px] flex-shrink-0 min-w-[148px] px-5 py-3 text-xs md:min-w-0 ${
                        isCompareSection ? navItemActive : ''
                      }`}
                    >
                      <span className={`${navIconBase} h-[18px] w-[18px] ${isCompareSection ? navIconActive : ''}`}>
                        {compareIcon}
                      </span>
                      <span className="flex-1 leading-5">Compare Portfolios</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate('/profile/password')}
              className={`${navItemBase} flex-shrink-0 min-w-[148px] md:min-w-0 ${
                path === '/profile/password' ? navItemActive : ''
              }`}
            >
              <span className={`${navIconBase} ${path === '/profile/password' ? navIconActive : ''}`}>
                {settingsIcon}
              </span>
              <span className="flex-1 leading-5">Settings</span>
            </button>
          </nav>
        </aside>
        <main className="min-w-0 flex-1 overflow-y-auto">
          <Routes>
            <Route path="password" element={<ChangePassword />} />
            <Route
              path="portfolios/*"
              element={
                portfolioEnabled ? (
                  <PortfoliosRoutes />
                ) : (
                  <AccessDeniedPage featureName="Portfolios" />
                )
              }
            />
            <Route
              path=""
              element={
                <Navigate
                  to={
                    portfolioEnabled
                      ? '/profile/portfolios'
                      : '/profile/password'
                  }
                  replace
                />
              }
            />
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    portfolioEnabled
                      ? '/profile/portfolios'
                      : '/profile/password'
                  }
                  replace
                />
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
